import React from 'react';
import { Column, ColumnShape } from 'react-base-table';

import {
  formatAttributeType,
  formatBlockEventType,
  formatTimestamp,
} from '@src/emp-3.0/utils/format';
import { categoryMap } from '@src/emp-3.0/constants/dimensions';

import {
  ActionsCellRenderer,
  CommonCellRenderer,
  ListCellRenderer,
  NameCellRenderer,
  RowNumberCellRenderer,
  CheckboxCellRenderer,
  StatusCellRenderer,
} from './components/CellRenderers';
import {
  CommonHeaderRenderer,
  DateHeaderRender,
  RowNumberHeaderRenderer,
  SelectableHeaderRenderer,
  CheckboxHeaderRenderer,
} from './components/HeaderRenderers';

interface IGenerateCommonColumnProps extends Partial<ColumnShape<any>> {}

// dateKey: 'created_by' | 'updated_by' | 'block_by' | '[mapped_event].event_name' | '[block_video_event].attribute_name'
export const generateCommonColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return {
    key: 'common_selectable_header',
    width: 200,
    flexGrow: 1,
    flexShrink: 1,
    align: Column.Alignment.LEFT,
    headerRenderer: SelectableHeaderRenderer,
    cellRenderer: CommonCellRenderer,
    sortEnabled: true,
    ...props,
  };
};

export const generateNumberColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return {
    title: '#',
    key: 'row-number',
    dataKey: 'row_number',
    width: 55,
    align: Column.Alignment.LEFT,
    headerRenderer: RowNumberHeaderRenderer,
    cellRenderer: RowNumberCellRenderer,
    resizable: true,
    ...props,
  };
};

// The 'id' field is required in the property 'data'.
export const generateCheckboxColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return {
    title: 'selection',
    key: 'selection',
    dataKey: 'event_name',
    width: 50,
    align: Column.Alignment.LEFT,
    headerRenderer: CheckboxHeaderRenderer,
    cellRenderer: CheckboxCellRenderer,
    resizable: true,
    ...props,
  };
};

export const generateNameColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return {
    key: 'name',
    width: 200,
    flexGrow: 2,
    flexShrink: 1,
    align: Column.Alignment.LEFT,
    headerRenderer: CommonHeaderRenderer,
    cellRenderer: NameCellRenderer,
    resizable: true,
    sortEnabled: true,
    showDraftPrefix: true,
    ...props,
  };
};

export const generateStatusColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return generateCommonColumn({
    title: 'Status',
    key: `status`,
    dataKey: 'status',
    width: 130,
    cellRenderer: StatusCellRenderer,
    ...props,
  });
};

export const generateCategoryColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return generateCommonColumn({
    title: 'Category',
    key: 'category',
    dataKey: 'category',
    width: 140,
    cellRenderer: ({ cellData, ...otherProps }) => (
      <CommonCellRenderer cellData={categoryMap[cellData] ?? cellData} {...otherProps} />
    ),
    ...props,
  });
};

// dataKey: '[dimension].attribute_type' | '[block_event].block_rules'
export const generateTypeColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return generateCommonColumn({
    title: 'Type',
    key: 'type',
    dataKey: 'attribute_type',
    width: 140,
    cellRenderer: ({ cellData, ...otherProps }) => (
      <CommonCellRenderer
        cellData={formatAttributeType(cellData) ?? formatBlockEventType(cellData) ?? cellData}
        {...otherProps}
      />
    ),
    ...props,
  });
};

// dataKey: '[metrics].related_event' | '[flow].related_events' | '[dimension].mapped_event_names' | '[mapped_event].related_metrics' | '[mapped_event].related_event'
export const generateListColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return {
    key: 'list',
    width: 200,
    flexGrow: 1,
    flexShrink: 1,
    headerRenderer: CommonHeaderRenderer,
    cellRenderer: ListCellRenderer,
    sortEnabled: false,
    ...props,
  };
};

// dateKey: 'updated_at' | 'deployed_at' | 'block_timestamp'
export const generateDateColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return {
    key: 'date',
    width: 136,
    flexGrow: 1,
    flexShrink: 1,
    align: Column.Alignment.LEFT,
    headerRenderer: DateHeaderRender,
    cellRenderer: ({ cellData, ...otherProps }) => (
      <CommonCellRenderer cellData={formatTimestamp(cellData)} {...otherProps} />
    ),
    sortEnabled: true,
    ...props,
  };
};

export const generateActionsColumn = (props?: IGenerateCommonColumnProps): ColumnShape<any> => {
  return {
    title: 'Operation',
    key: 'operation',
    width: 180,
    align: Column.Alignment.RIGHT,
    headerRenderer: <div />,
    cellRenderer: ActionsCellRenderer,
    cloneVisible: true,
    deployVisible: true,
    undeployVisible: true,
    deleteVisible: true,
    unblockVisible: false,
    ...props,
  };
};

interface IGenerateColumnProps extends IGenerateCommonColumnProps {
  columnType: string;
}
export const generateColumn = ({ columnType, ...config }: IGenerateColumnProps) => {
  const columnGenerators: Record<string, (config: any) => any> = {
    common: generateCommonColumn,
    number: generateNumberColumn,
    checkbox: generateCheckboxColumn,
    name: generateNameColumn,
    status: generateStatusColumn,
    category: generateCategoryColumn,
    type: generateTypeColumn,
    list: generateListColumn,
    date: generateDateColumn,
    actions: generateActionsColumn,
  };

  const generator = columnGenerators[columnType];
  if (!generator) {
    throw new Error(`Unknown column type: ${columnType}`);
  }
  return generator(config);
};
